import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import profileAssignmentService, {
    type EmployeePickerResponse,
    type EmployeePickerFilterParams,
    type ProfileAssignment,
    type ProfileAssignmentCount,
    type ProfileAssignmentPayload,
    type RosterEmployee,
    type RosterFilterParams,
} from './profileAssignmentService';

import type { QueryClient } from '@tanstack/react-query';

import { CompetencyQueryKey, ProfileAssignmentQueryKey } from '@/constants/QueryKeys';

// Must stay within the backend's PaginationSettings.MaximumPageSize (50). A larger
// value makes GET /EmployeeDirectory/employees reject the request with HTTP 400
// (PaginatedRequestValidator: PageSize InclusiveBetween(1, 50)), which surfaced as
// "Unable to load employees." in the assign-to-employees wizard.
const EMPLOYEE_PAGE_SIZE = 12;

const useGetCompanyEmployees = (
    search: string,
    enabled = true,
    filters: EmployeePickerFilterParams = {},
    competencyId?: string,
    groupId?: string,
    page = 1,
) => {
    return useQuery<EmployeePickerResponse>({
        queryKey: [
            ProfileAssignmentQueryKey.EMPLOYEES,
            search,
            filters,
            competencyId,
            groupId,
            page,
        ],
        queryFn: () =>
            profileAssignmentService.getEmployees({
                pageNumber: page,
                pageSize: EMPLOYEE_PAGE_SIZE,
                searchQuery: search,
                isActiveOnly: true,
                includeInvited: true,
                ...filters,
                competencyId,
                groupId,
            }),
        enabled,
        placeholderData: keepPreviousData,
    });
};

// Creating and removing an assignment move the same read models (assignment lists,
// roster sections, per-profile counts, "Assignée à N profils" badges, competency
// impact), so both mutations share the same invalidation set.
// Uses the active QueryClient from React context so nested providers (e.g. the
// Performance Profile MFE shell) refresh their own cache, not a global singleton.
const invalidateAssignmentReadModels = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
        queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
    });

    queryClient.invalidateQueries({
        queryKey: [ProfileAssignmentQueryKey.ASSIGNMENTS],
    });

    queryClient.invalidateQueries({
        queryKey: [ProfileAssignmentQueryKey.ROSTER],
    });

    queryClient.invalidateQueries({
        queryKey: [ProfileAssignmentQueryKey.STATISTICS],
    });

    queryClient.invalidateQueries({
        queryKey: [CompetencyQueryKey.COMPETENCY_IMPACT],
    });
};

const useAssignProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            profileId,
            payload,
        }: {
            profileId: string;
            payload: ProfileAssignmentPayload;
        }) => profileAssignmentService.assignProfile(profileId, payload),
        onSuccess: () => invalidateAssignmentReadModels(queryClient),
    });
};

const useUnassignProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ profileId, employeeIds }: { profileId: string; employeeIds: string[] }) =>
            profileAssignmentService.unassignProfile(profileId, employeeIds),
        onSuccess: () => invalidateAssignmentReadModels(queryClient),
    });
};

// The employee-centric roster, filtered by company attributes + search. Returns a
// flat array; the page splits it into the two sections and paginates client-side.
const useGetRoster = (filters: RosterFilterParams, enabled = true) => {
    return useQuery<RosterEmployee[]>({
        queryKey: [ProfileAssignmentQueryKey.ROSTER, filters],
        queryFn: () => profileAssignmentService.getRoster(filters),
        enabled,
        placeholderData: keepPreviousData,
    });
};

// Per-profile assigned-employee counts (one fetch for the whole company), used by the
// "Profils assignés" modal to show each profile's "N employés" without per-profile requests.
const useGetProfileAssignmentStatistics = (enabled = true) => {
    return useQuery<ProfileAssignmentCount[]>({
        queryKey: [ProfileAssignmentQueryKey.STATISTICS],
        queryFn: () => profileAssignmentService.getAssignmentStatistics(),
        enabled,
    });
};

// The employee ids assigned to a single profile, used by the "Profils assignés" modal's
// per-profile employee drill-in.
const useGetProfileAssignments = (profileId: string, enabled = true) => {
    return useQuery<ProfileAssignment[]>({
        queryKey: [ProfileAssignmentQueryKey.ASSIGNMENTS, profileId],
        queryFn: () => profileAssignmentService.getAssignments(profileId),
        enabled,
    });
};

export type { EmployeePickerFilterParams };

export {
    EMPLOYEE_PAGE_SIZE,
    useGetCompanyEmployees,
    useAssignProfile,
    useUnassignProfile,
    useGetProfileAssignmentStatistics,
    useGetProfileAssignments,
    useGetRoster,
};
